import { useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize from 'rehype-sanitize';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Flatten react-markdown / highlight children to plain text for copy. */
function nodeToText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (typeof node === 'object' && 'props' in node) {
    const el = node as { props?: { children?: ReactNode } };
    return nodeToText(el.props?.children);
  }
  return '';
}

function CodeBlock({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'code'>) {
  const [copied, setCopied] = useState(false);
  const text = nodeToText(children).replace(/\n$/, '');
  const isBlock = Boolean(
    (className && className.includes('language-')) || text.includes('\n'),
  );

  if (!isBlock) {
    return (
      <code
        className={cn(
          'text-[#B7B1B1] bg-[#141414] px-1.5 py-0.5 rounded text-[13px]',
          className,
        )}
        {...props}
      >
        {children}
      </code>
    );
  }

  const lang = /language-([\w+-]+)/.exec(className || '')?.[1] || '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="group/code relative my-3 rounded-xl border border-[#2a2a2a] bg-[#111111] overflow-hidden not-prose">
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-[#1e1e1e] bg-[#0e0e0e]">
        <span className="text-[10px] uppercase tracking-wider text-[#4a4a4a] font-medium truncate">
          {lang || 'code'}
        </span>
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
          title="Copy code"
        >
          {copied ? (
            <>
              <Check className="w-3 h-3 text-green-500/80" />
              <span className="text-green-500/80">Copied</span>
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto p-3 m-0 text-[13px] leading-relaxed scrollbar-thin">
        <code className={cn('hljs', className)} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
}

function PreBlock({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}

export function MarkdownContent({
  content,
  className,
  isStreaming,
}: {
  content: string;
  className?: string;
  isStreaming?: boolean;
}) {
  const safe = content ?? '';

  return (
    <div
      className={cn(
        'text-[15px] text-[#e8e6e3] leading-[1.7] prose prose-sm prose-invert max-w-none',
        'prose-pre:bg-transparent prose-pre:border-0 prose-pre:p-0 prose-pre:m-0',
        'prose-code:before:content-none prose-code:after:content-none',
        'prose-a:text-[#8ab4f8] prose-a:no-underline hover:prose-a:underline',
        'prose-headings:text-[#e8e6e3] prose-headings:font-medium',
        'prose-strong:text-[#e8e6e3]',
        'prose-p:my-2.5 prose-ul:my-2.5 prose-ol:my-2.5',
        'prose-table:text-[13px] prose-th:border prose-th:border-[#2a2a2a] prose-td:border prose-td:border-[#2a2a2a]',
        className,
      )}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeSanitize,
          [rehypeHighlight, { detect: false, ignoreMissing: true }],
        ]}
        components={{
          code: CodeBlock,
          pre: PreBlock,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {safe}
      </Markdown>
      {isStreaming && (
        <span className="od-caret inline-block w-1.5 h-4 ml-0.5 bg-[#8a8884] align-middle rounded-sm" />
      )}
    </div>
  );
}
